







































































































































































// core
import { mapActions } from 'vuex';
import { get, sync } from 'vuex-pathify';

// types and classes
import { Module } from '@/models/configurator/module';
import { Question, QuestionType } from '@/models/configurator/question';

// utils helpers
import mixins from '@/utils/mixins';
import componentState from '@/mixins/state';
import {
  Configuration,
  ConfigurationAnswer,
  ConfigurationItem
} from '@/models/configurator/configuration';

// components
import ModuleArchivedBanner from './modules/ModuleArchivedBanner.vue';

enum STEP {
  MODULE = 1,
  QUESTION = 2,
  SPARE_PARTS = 3,
  IS_ACCESSORY = 4
}

export default mixins(componentState).extend({
  name: 'ConfigurationFormDialog',

  components: { ModuleArchivedBanner },

  props: {
    module: Number
  },

  data: () => ({
    valid: true,
    dialog: false,

    moduleLoading: false,
    configurationLoading: false,

    QuestionType,

    currentModule: null as null | Module,

    configuration: (null as unknown) as Configuration,

    step: STEP.MODULE,
    STEP,

    isAccessory: false,
    snapshot: undefined as undefined | number,

    exchangeRate: 1.0,
    currencyCode: 'USD',

    resolve: null as any,
    reject: null as any
  }),

  computed: {
    isSparePartsStep(): boolean {
      return this.step === STEP.SPARE_PARTS;
    },

    isProductStep(): boolean {
      return this.step === STEP.QUESTION;
    },

    isAccessoryStep(): boolean {
      return this.step === STEP.IS_ACCESSORY;
    },

    questions: get<Question[]>('configuratorQuestion/questions'),

    getModuleById: get<(id: number) => Module>(
      'configuratorModule/getModuleById'
    ),

    computedModules: get<Module[]>('configuratorModule/modules'),

    modules(): Module[] {
      let computedModules = [] as Module[];
      if (!this.module) {
        computedModules = this.computedModules;
      } else {
        computedModules = [this.getModuleById(this.module)];
      }
      return computedModules;
    },

    configurationItems: sync<ConfigurationItem[]>(
      'configuratorConfiguration/configurationItems'
    ),
    configurationAnswers: sync<ConfigurationAnswer[]>(
      'configuratorConfiguration/answers'
    ),

    getQuestionById: get<(module: number, id: number) => Question>(
      'configuratorQuestion/getQuestionById'
    )
  },

  // TODO 2021-01-06 - LH - have mounted property for edit to fetch current configuration items and answers when opening the dialog

  methods: {
    ...mapActions('configuratorAnswer', ['createAnswer']),

    ...mapActions('configuratorQuestion', [
      'fetchQuestions',
      'fetchQuestion',
      'deleteQuestion'
    ]),

    ...mapActions('configuratorModule', [
      'createModule',
      'fetchModule',
      'fetchModules',
      'updateModule'
    ]),

    ...mapActions('configuratorConfiguration', [
      'fetchConfiguration',
      'createConfiguration',
      'fetchConfigurationAnswers',
      'fetchConfigurationItems',
      'deleteConfiguration'
    ]),

    /** Open dialog for configuration dialog thats returns a fetched configuration
     *  if dialog is closed with no-Action parameters
     * @public
     * @param {Configuration} configuration configuration to use
     * @param {number?} moduleId moduleId to use
     */
    async open(
      configuration: Configuration,
      moduleId?: number,
      exchangeRate = 1.0,
      currencyCode = 'USD',
      isAccessory = false,
      snapshot?: number
    ) {
      try {
        this.exchangeRate = exchangeRate;
        this.currencyCode = currencyCode;
        this.isAccessory = isAccessory;
        this.snapshot = snapshot;
        this.setComponentState('loading');
        this.configuration = configuration;

        if (this.module ?? moduleId) {
          this.fetchModule(this.module ?? moduleId)
            .then(module => this.selectModule(module))
            .then(() => {
              this.getConfigurationItems();
              this.setComponentState('loaded');
            });
        } else {
          this.fetchModules().then(() => this.setComponentState('loaded'));
        }
      } catch (error) {
        this.setComponentState('error', error);
      } finally {
        this.dialog = true;
      }
      return new Promise<Configuration>((resolve, reject) => {
        this.resolve = resolve;
        this.reject = reject;
      });
    },

    async confirmDialog(title: string, message: string) {
      const confirm = (this as any).$refs.confirm;
      return await confirm.open(
        title,
        {
          right: {
            text: 'Yes'
          }
        },
        message
      );
    },

    /**
     * @public
     * @returns resolved data
     */
    async save() {
      //  fetch tp get updated configuration
      // bug on create would use current configuration
      // In case of the configuration loading the we dont fetch again making another api call
      if (!this.configurationLoading) {
        this.configurationLoading = true;
        //TODO: LF-2022-12-20 - Why are we fetching and passing the configuration back to the parent
        this.resolve(await this.fetchConfiguration(this.configuration.id));
        this.configurationLoading = false;
      }
      this.dialog = false;
    },

    async close(noActionNeeded?: boolean) {
      if (!noActionNeeded) {
        if (
          await this.confirmDialog(
            'Discard changes?',
            this.isProductStep
              ? 'All questions were not answered'
              : 'This cannot be undone'
          )
        ) {
          this.reject(this.configuration);
          this.dialog = false;
          this.reset();
          return;
        } else {
          return;
        }
      }
      this.save();
      this.reset();
    },

    // test new config
    getConfigurationItems() {
      this.configurationItems = [];
      this.configurationAnswers = [];
      this.fetchConfigurationItems(this.configuration.id).finally(() => {
        this.fetchConfigurationAnswers(this.configuration.id);
      });
    },

    async selectModule(module: Module, callback?: () => any) {
      this.moduleLoading = true;

      if (callback) await callback();
      this.currentModule = module;

      if (this.isAccessory || module.is_accessories) {
        this.step = STEP.IS_ACCESSORY;
      } else if (module.is_spare_parts) {
        this.step = STEP.SPARE_PARTS;
      } else {
        await this.fetchQuestions({
          module: module.id!,
          configuration: this.configuration.id
        })
          .then((questions: Question[]) => {
            this.step = STEP.QUESTION;
            if (questions.length) {
              // If we are editing then we want to set question to the finished step
              // to allow the user to select any question
              // TODO: 2021-01-17 - LH - check if we have to make an extra api cal
              this.fetchQuestion({
                module: module.id,
                id: questions[0].id,
                configuration: this.configuration.id
              });
            }
          })
          .catch((err: Error) => {
            this.setComponentState('error', err);
          })
          .finally(() => {
            if (callback) callback();
            this.moduleLoading = false;
          });
      }
    },

    reset() {
      this.step = STEP.MODULE;
      this.configurationItems = [];
      this.configurationAnswers = [];
    }
  }
});
